"use client";

// WO-GF-001-P1B-S13 (RW3-1 · founder ruling (c) "RW2-6 GO") — the CARD FUNNEL.
// Supersedes the S11 single-screen quick-view (RW-4/5/6 · F77) and closes F82 (the RENTALS variant).
//
// THE FOUNDER FINDING, and what the live walk actually showed (evidence: §S13 ledger, probe captures):
//   "first step shows the company's product list, THEN you add; Continue must advance to the next slide,
//    NOT add directly."  — correct, and live works exactly that way:
//   STEP 1  `.modalBodyContainer.scndScreen` — supplier header (category · city · View Profile · logo ·
//           name · description) + the company's PRODUCT LIST (selectable rows, member price right) +
//           `View in Store` + `Continue`. RENTALS suppliers also carry a SKI · BOARD · OTHER tab row.
//           Selecting a second product DESELECTS the first (live's checkboxes behave as single-select —
//           verified: checking rows 1 then 2 leaves only row 2 checked), and `Continue` with nothing
//           selected is a NO-OP (verified: the modal does not change).
//   STEP 2  `.modalBodyContainer.rentalProductModalBody` — the chosen product: name, description,
//           Guest/Member price, its booking fields (activities: Desired Date + a real Desired Time select;
//           rentals: Starting + Ending), Quantity −/+, and the orange **Add**.
//
// ENTRY POINT DOES NOT CHANGE THE STEPS. The WO asked for marketplace-card and carousel-card to be
// investigated separately; they were, and live returns byte-identical step 1 + step 2 from both (same
// classes, same CTAs, same product list). The axis that DOES change the popup is the product CATEGORY —
// exactly the correction S12 recorded as F82. Reported rather than split into two code paths that would
// have had to be identical anyway.
//
// RAILS (crown-jewel adjacency — this funnel ends in a cart add):
//   • ZERO NEW CART CODE. Add calls `useCartStore.getState().addItem({…})` with the EXACT CartItem shape
//     `MarketplaceSupplierDetail.readRow()` builds. Same store, same booking path, same line identity —
//     adding a product here and from its detail row UPSERTS one line (lineId collapses to listingId).
//   • PIXEL-NEUTRAL AT REST: served cards are never mutated on mount; the popup is portalled on click only.
//   • DATA IS PORTED, NEVER INVENTED — see lib/quickview-products.ts for the extraction + the live
//     cross-check (TAG snow-activity 4/4 · Spicy Sports ski 9/9, names and prices exact).
//
// Harness hooks: data-behavior="quick-view" (card) · [data-quickview-popup] · [data-quickview-step]
// [data-quickview-type-tab] · [data-quickview-product] · [data-quickview-continue] · [data-quickview-add]
// [data-quickview-profile] · [data-quickview-store].

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ModalShell, btn } from "@/components/site/ModalShell";
import { useCartStore } from "@/lib/cart-store";
import { gaItemFromProduct, trackViewItem } from "@/lib/analytics-event";
import type { QuickViewListing } from "@/lib/quickview-index";
import type { QuickViewProduct, QuickViewSupplier } from "@/lib/quickview-products";

type Opened = {
  listing: QuickViewListing;
  supplier: QuickViewSupplier;
  /** the card's own h3 token, e.g. "SNOW-ACTIVITY" / "RENTALS" — selects the step-1 product set. */
  cardToken: string;
  heading: string;
  /** S35 · F220 — the supplier logo AS THE CARD ALREADY SERVES IT. Live's popup shows this same
   *  asset (measured 100×65); taking it off the clicked card means no second source to drift. */
  logoSrc: string;
};

const TEAL = "#71abb8";
const GREY = "#555";
type ProfileTab = { label: string; token: string };
/** Presentation taxonomy only: Strapi listings and cart IDs remain the purchasable source of truth. */
// QA-BATCH-2 · items 4/5 — the founder's fixed sets, in his order: Boards · Skis · Other · All and
// Snowmobile · Snowshoe · Dogsled · Other · All. The same spelling the design export's profile tabs use.
export const RENTAL_PROFILE_TABS: ProfileTab[] = [
  { label: "Boards", token: "snowboard" }, { label: "Skis", token: "ski" },
  { label: "Other", token: "other" }, { label: "All", token: "*" },
];
export const ACTIVITY_PROFILE_TABS: ProfileTab[] = [
  { label: "Snowmobile", token: "snowmobile" }, { label: "Snowshoe", token: "snowshoe" },
  { label: "Dogsled", token: "dog-sled" }, { label: "Other", token: "other" }, { label: "All", token: "*" },
];
/** Shared filter preserves an honest empty category instead of substituting unrelated products. */
export function profileProductsForTab(products: QuickViewProduct[], variant: string, token: string): QuickViewProduct[] {
  if (token === "*") return products;
  const core = variant === "equipment-rentals" ? ["ski", "snowboard"] : ["snowshoe", "snowmobile", "dog-sled"];
  return token === "other"
    ? products.filter((p) => !core.some((type) => p.types.includes(type)))
    : products.filter((p) => p.types.includes(token));
}

/**
 * RUN001 activation — the ONE predicate both steps read. A product is purchasable here only when the server-side join
 * said so (`purchasable === true`, purchase-truth.ts) AND handed over the identity the booking needs. Anything else is
 * OFF: the product stays listed with its prices, and no add control exists for it (not disabled by colour alone — absent).
 */
export function quickViewPurchasable(product: Pick<QuickViewProduct, "purchasable" | "listingId">): boolean {
  return product.purchasable === true && typeof product.listingId === "string" && product.listingId.trim() !== "";
}

/** the integer dollars in a price node, e.g. "$142" / "127" → 142 / 127. */
function money(text: string | null | undefined): number | null {
  const m = /(\d+(?:\.\d+)?)/.exec((text || "").replace(/,/g, ""));
  return m?.[1] ? Number(m[1]) : null;
}

export default function QuickViewLayer({
  listings,
  suppliers,
  mountain,
  place,
}: {
  listings: QuickViewListing[];
  suppliers: QuickViewSupplier[];
  mountain: string;
  /** QA-BATCH-2 · item 3 — the mountain/location context shown beside the vendor name ("Whistler, BC"),
   *  read from the served mountain record for THIS page's mountain rather than a literal. */
  place?: string;
}) {
  const [open, setOpen] = useState<Opened | null>(null);
  const byLogo = useMemo(
    () => new Map(suppliers.map((s) => [s.logo, s])),
    [suppliers],
  );

  useEffect(() => {
    if (!listings.length) return;
    const cleanups: Array<() => void> = [];

    for (const card of Array.from(
      document.querySelectorAll<HTMLElement>(".cardBorder"),
    )) {
      // read the card's own identity out of the served DOM (never mutated here).
      const logoSrc =
        card.querySelector<HTMLImageElement>('img[src*="supplier/"]')?.getAttribute("src") || "";
      const logo = logoSrc.split("/").pop() || "";
      if (!logo) continue;
      const member = money(card.querySelector("strong")?.textContent);
      const guest = money(card.querySelector("del")?.textContent);
      // (logo, member, guest) identifies the listing precisely — live-verified join.
      const listing = listings.find(
        (l) =>
          l.logo === logo &&
          (member == null || l.memberPrice === member) &&
          (guest == null || l.guestPrice === guest),
      );
      const supplier = byLogo.get(logo);
      // no listing OR no ported product catalogue ⇒ the card stays exactly as captured (no half-funnel).
      if (!listing || !supplier || !supplier.products.length) continue;

      const h3 = card.querySelector("h3");
      const cardToken = (h3?.getAttribute("title") || h3?.textContent || "").trim();
      const heading = (h3?.textContent || listing.categoryName).trim();
      const oldAlign = h3?.style.textAlign ?? "";
      if (h3) { h3.style.textAlign = "center"; h3.setAttribute("data-quickview-card-title", ""); }

      card.setAttribute("data-behavior", "quick-view");
      const onClick = (e: Event) => {
        // a real link inside the card must keep navigating (never hijack a working route).
        if ((e.target as HTMLElement | null)?.closest("a[href]")) return;
        e.preventDefault();
        e.stopPropagation();
        setOpen({ listing, supplier, cardToken, heading, logoSrc });
      };
      card.addEventListener("click", onClick);
      cleanups.push(() => { card.removeEventListener("click", onClick); if (h3) { h3.style.textAlign = oldAlign; h3.removeAttribute("data-quickview-card-title"); } });
    }

    return () => cleanups.forEach((c) => c());
  }, [listings, byLogo]);

  const close = useCallback(() => setOpen(null), []);

  if (!open) return <span data-quickview-layer hidden />;
  return (
    <>
      <span data-quickview-layer hidden />
      {createPortal(
        <CardFunnel data={open} mountain={mountain} place={place} onClose={close} />,
        document.body,
      )}
    </>
  );
}

function CardFunnel({
  data,
  mountain,
  place,
  onClose,
}: {
  data: Opened;
  mountain: string;
  place?: string;
  onClose: () => void;
}) {
  const { listing, supplier, cardToken, heading, logoSrc } = data;
  const isRental = supplier.variant === "equipment-rentals";

  // RENTALS cards carry a CATEGORY, not a product type → live shows the tab row and opens on the first
  // tab that actually has stock. Every other card's token IS a product type and filters directly.
  const token = cardToken.toLowerCase();
  const directSet = supplier.products.filter((p) => p.types.includes(token));
  const tabs = isRental ? RENTAL_PROFILE_TABS : ACTIVITY_PROFILE_TABS;
  const [tab, setTab] = useState<string>("*");

  const shown = useMemo(() => {
    if (isRental || tab !== "*") return profileProductsForTab(supplier.products, supplier.variant, tab);
    return directSet.length ? directSet : supplier.products;
  }, [isRental, tab, supplier.products, supplier.variant, directSet]);

  const [selected, setSelected] = useState<QuickViewProduct | null>(null);
  const [step, setStep] = useState<1 | 2>(1);

  return (
    <div data-quickview-popup data-quickview-step={step}>
      <ModalShell title={heading || listing.categoryName} onClose={onClose}>
        {step === 1 ? (
          <StepOne
            listing={listing}
            supplier={supplier}
            place={place}
            logoSrc={logoSrc}
            isRental={isRental}
            tabs={tabs}
            tab={tab}
            setTab={(t) => {
              setTab(t);
              setSelected(null); // switching type clears the selection, as live does
            }}
            shown={shown}
            selected={selected}
            setSelected={setSelected}
            mountain={mountain}
            onContinue={() => selected && setStep(2)}
            onDetails={(product) => { setSelected(product); setStep(2); }}
          />
        ) : (
          <StepTwo
            product={selected!}
            supplier={supplier}
            mountain={mountain}
            onBack={() => setStep(1)}
            onClose={onClose}
          />
        )}
      </ModalShell>
    </div>
  );
}

/** STEP 1 — supplier header + the company's product list (live's `.scndScreen`). */
function StepOne({
  listing,
  supplier,
  isRental,
  tabs,
  tab,
  setTab,
  shown,
  selected,
  setSelected,
  mountain,
  place,
  onContinue,
  onDetails,
  logoSrc,
}: {
  listing: QuickViewListing;
  supplier: QuickViewSupplier;
  logoSrc: string;
  isRental: boolean;
  tabs: ProfileTab[];
  tab: string;
  setTab: (t: string) => void;
  shown: QuickViewProduct[];
  selected: QuickViewProduct | null;
  setSelected: (p: QuickViewProduct | null) => void;
  mountain: string;
  place?: string;
  onContinue: () => void;
  onDetails: (product: QuickViewProduct) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 280 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* S35 · F220 — THE SUPPLIER LOGO. Live's pop-up carries it (measured 100×65 at 1440×900,
              read-only); the build's carried NO image at all. The src is the one the clicked card is
              already serving, so there is no second source to drift and nothing new to fetch. */}
          {logoSrc && (
            // eslint-disable-next-line @next/next/no-img-element
            <img data-quickview-logo src={logoSrc} alt={supplier.supplierName} style={{ width: 100, height: 65, objectFit: "contain", flex: "0 0 auto" }} />
          )}
          <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: TEAL }}>
            {supplier.supplierName}
          </div>
          {(listing.categoryName || place) && (
            <div data-quickview-place style={{ fontSize: 12, color: GREY }}>
              {[listing.categoryName, place].filter(Boolean).join(" · ")}
            </div>
          )}
          </div>
        </div>
        <a
          data-quickview-profile
          href={supplier.profileHref}
          target="_blank"
          rel="noopener noreferrer"
          className="rightsectionlink"
          style={{ fontSize: 13, color: TEAL, whiteSpace: "nowrap" }}
        >
          View Profile
        </a>
      </div>

      {/* S35 · F220 — F79 IS RETRACTED, WITH THE MEASUREMENT THAT RETRACTS IT.
          F79 recorded that "live's popup carries a SUPPLIER-level description no ported artifact or BE
          field exposes", and this rendered the joined LISTING's prose instead. Measured live vs build
          at 1440×900 (read-only): live's pop-up shows THE ADVENTURE GROUP's own supplier blurb, the
          build showed the Whistler Snowshoeing Adventure product blurb — two different paragraphs on
          the same click. And the artifact does expose it: it is in the same `detail/*.detail.json`
          quickview-products already parses, which is why the supplier profile page has been rendering
          it all along. The listing's own prose stays as the FALLBACK, so a capture without a supplier
          paragraph degrades to what it had rather than to nothing. */}
      {(supplier.description || listing.description) && (
        <p data-quickview-desc style={{ fontSize: 13, lineHeight: 1.5, color: GREY, margin: 0, maxHeight: 150, overflowY: "auto" }}>
          {supplier.description || listing.description}
        </p>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {tabs.map((t) => (
            <button
              key={t.token}
              type="button"
              data-quickview-type-tab={t.token}
              aria-pressed={tab === t.token}
              onClick={() => setTab(t.token)}
              style={{
                padding: "6px 14px",
                borderRadius: 999,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
                border: `1px solid ${tab === t.token ? TEAL : "#ccc"}`,
                background: tab === t.token ? TEAL : "#fff",
                color: tab === t.token ? "#fff" : GREY,
              }}
            >
              {t.label}
            </button>
          ))}
      </div>

      <div style={{ fontSize: 11, color: GREY, textAlign: "right", fontWeight: 700 }}>Member</div>
      <ul
        data-quickview-products
        style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: 260, overflowY: "auto" }}
      >
        {/* FOUNDER-WALK-QA-STAGING-007 · A5 — the fixed three-tab row (F82) means OTHER can select a
            category this supplier stocks nothing in (measured: every ported rental supplier is 100%
            ski/board). An empty list under a live tab read as a broken list; it now says what it is. */}
        {shown.length === 0 ? (
          <li data-quickview-empty style={{ padding: "8px 0", fontSize: 12, color: "#6b7580" }}>
            Nothing in this category from this supplier.
          </li>
        ) : null}
        {shown.map((p) => (
          <li
            key={p.key || p.name}
            data-quickview-product={p.key || p.name}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "6px 0", borderBottom: "1px solid #eee" }}
          >
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flex: 1 }}>
              <input
                type="checkbox"
                className="form-check-input"
                // live's list is single-select despite rendering checkboxes (verified on production).
                checked={selected?.key === p.key && selected?.name === p.name}
                onChange={(e) => setSelected(e.target.checked ? p : null)}
              />
              <span style={{ fontSize: 13 }}>{p.name}</span>
            </label>
            <button type="button" data-quickview-details={p.key || p.name} onClick={() => onDetails(p)} style={{ border: "none", background: "none", color: TEAL, cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>Details</button>
            {/* founder 2026-11-03: the selling flag alone decides. OFF is information only, with no price. */}
            {quickViewPurchasable(p) && (
              <span className="link-orange smallText modal-rental-price" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
                $ {p.memberPrice ?? p.guestPrice ?? ""}
              </span>
            )}
          </li>
        ))}
      </ul>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <a
          data-quickview-store
          href={`/mountain-${mountain}/deals-and-packages`}
          className="btn btn-outline-secondary"
          style={{ ...btn("outline"), textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
        >
          View in Store
        </a>
        <button
          type="button"
          data-quickview-continue
          onClick={onContinue}
          // live: Continue with nothing selected does nothing at all (no advance, no add).
          disabled={!selected}
          style={{ ...btn("primary"), opacity: selected ? 1 : 0.5, cursor: selected ? "pointer" : "not-allowed" }}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

/** STEP 2 — the selected product + its booking fields + Add (live's `.rentalProductModalBody`). */
function StepTwo({
  product,
  supplier,
  mountain,
  onBack,
  onClose,
}: {
  product: QuickViewProduct;
  supplier: QuickViewSupplier;
  mountain: string;
  onBack: () => void;
  onClose: () => void;
}) {
  const isRental = supplier.variant === "equipment-rentals";
  // S16 · F97 — the date fields now start EMPTY. They used to be prefilled with today(), which is what
  // let a booking be added without the user ever choosing a date (the founder's finding). An empty
  // field also makes the requirement visible instead of silently booking "today".
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [time, setTime] = useState("");
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  // GA4 view_item — this step IS one product's detail (its own description, price and booking fields).
  useEffect(() => {
    trackViewItem(gaItemFromProduct(product, supplier.supplierName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.key]);

  // F97 — a date is REQUIRED before Add; rentals need both ends of the hire, and the return cannot
  // precede the start.
  const missing = !start || (isRental && !end) || (!isRental && supplier.timeOptions.length > 0 && !time);
  const badRange = isRental && !!start && !!end && end < start;
  // RUN001 activation — the backend purchase truth is the first AND. OFF renders no Add control at all (see below), so a
  // click or a keyboard activation has nothing to reach; the guard here is for a programmatic call.
  const purchasable = quickViewPurchasable(product);
  const canAdd = purchasable && !missing && !badRange;

  // THE add — the existing B3.4 cart path, same CartItem shape as MarketplaceSupplierDetail.readRow().
  const add = () => {
    if (!canAdd) return;
    useCartStore.getState().addItem({
      key: product.key,
      listingId: product.listingId,
      title: product.name,
      guestPrice: product.guestPrice,
      memberPrice: product.memberPrice,
      qty,
      startDate: start,
      endDate: isRental ? end : "",
      depTime: isRental ? undefined : time,
      mountain,
      supplier: supplier.supplierName,
      supplierSlug: supplier.supplierSlug,
    });
    setAdded(true);
    // F99 — the popup closes itself after a successful add (it used to sit there until the user
    // dismissed it). Brief pause so the "Added!" confirmation is actually seen.
    window.setTimeout(onClose, 650);
  };

  const field: React.CSSProperties = {
    border: "1px solid #ccc",
    borderRadius: 6,
    padding: "6px 8px",
    fontSize: 13,
    width: "100%",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 280 }}>
      <button
        type="button"
        data-quickview-back
        onClick={onBack}
        style={{ alignSelf: "flex-start", border: "none", background: "none", color: TEAL, cursor: "pointer", fontSize: 13, padding: 0 }}
      >
        ‹ Back
      </button>

      <div style={{ fontWeight: 700, fontSize: 16 }}>{product.name}</div>
      {product.description && (
        <p style={{ fontSize: 13, lineHeight: 1.5, color: GREY, margin: 0, maxHeight: 170, overflowY: "auto" }}>
          {product.description}
        </p>
      )}

      <div style={{ display: "flex", gap: 24, alignItems: "baseline" }}>
        {purchasable && product.guestPrice != null && (
          <div>
            <div style={{ fontSize: 11, color: GREY }}>Guest Price</div>
            <strong style={{ fontSize: 18 }}>${product.guestPrice} CAD</strong>
          </div>
        )}
        {purchasable && product.memberPrice != null && (
          <div>
            <div style={{ fontSize: 11, color: GREY }}>Member Price</div>
            <strong style={{ fontSize: 18, color: "#e87722" }}>${product.memberPrice} CAD</strong>
          </div>
        )}
      </div>

      {/* Booking fields, per live: rentals = Starting + Ending; activities = Desired Date + Desired Time
          (its options read verbatim off the captured rows — never invented). */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 150 }}>
          <div style={{ fontSize: 12, color: GREY, marginBottom: 4 }}>
            {isRental ? "Starting:" : "Desired Date:"}
          </div>
          <input
            type="date"
            data-quickview-start
            value={start}
            onChange={(e) => setStart(e.target.value)}
            style={field}
          />
        </div>
        {isRental ? (
          <div style={{ flex: 1, minWidth: 150 }}>
            <div style={{ fontSize: 12, color: GREY, marginBottom: 4 }}>Ending:</div>
            <input
              type="date"
              data-quickview-end
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              style={field}
            />
          </div>
        ) : (
          supplier.timeOptions.length > 0 && (
            <div style={{ flex: 1, minWidth: 150 }}>
              <div style={{ fontSize: 12, color: GREY, marginBottom: 4 }}>Desired Time:</div>
              <select
                data-quickview-time
                className="form-select"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                style={field}
              >
                <option value="" disabled>Choose a time</option>
                {supplier.timeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          )
        )}
        <div style={{ minWidth: 120 }}>
          <div style={{ fontSize: 12, color: GREY, marginBottom: 4 }}>Participants:</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button type="button" data-quickview-qty-dec onClick={() => setQty((q) => Math.max(1, q - 1))} style={{ ...field, width: 32, cursor: "pointer" }}>
              −
            </button>
            <span data-quickview-qty style={{ fontSize: 14, minWidth: 16, textAlign: "center" }}>{qty}</span>
            <button type="button" data-quickview-qty-inc onClick={() => setQty((q) => Math.min(10, q + 1))} style={{ ...field, width: 32, cursor: "pointer" }}>
              +
            </button>
          </div>
        </div>
      </div>
      <p data-quickview-summary style={{ margin: 0, fontSize: 12, color: GREY }}>{qty} participant{qty === 1 ? "" : "s"}{start ? ` · ${start}` : " · choose a date"}{!isRental && supplier.timeOptions.length > 0 ? (time ? ` · ${time}` : " · choose a time") : null}{isRental && end ? ` to ${end}` : null}</p>

      {/* F97 — say WHY Add is unavailable rather than leaving a dead-looking button. */}
      {purchasable && !canAdd && (
        <div data-quickview-hint style={{ fontSize: 12, color: "#b23b3b" }}>
          {badRange
            ? "The ending date cannot be before the starting date."
            : isRental
              ? "Choose your starting and ending dates to add this to your cart."
              : supplier.timeOptions.length > 0 && !time ? "Choose a date and time to add this to your cart." : "Choose a date to add this to your cart."}
        </div>
      )}
      {/* OFF is not a greyed button: the Add control and both prices are not rendered; the product and its copy stay. */}
      {purchasable ? (
        <button
          type="button"
          data-quickview-add
          disabled={!canAdd || added}
          aria-disabled={!canAdd || added}
          onClick={add}
          className="btn btn-warning"
          style={{
            ...btn("primary"),
            background: canAdd ? "#ffc107" : "#e6e6e6",
            color: canAdd ? "#111" : "#8a8f9a",
            textTransform: "none",
            cursor: canAdd && !added ? "pointer" : "not-allowed",
          }}
        >
          {added ? "Added!" : "Add"}
        </button>
      ) : null}
    </div>
  );
}
